import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
} from '@mui/material';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import pxToRem from 'assets/theme/function/px-to-rem';
import { CardListRequest } from 'types/card/card-list';
import { useStatusListQuery } from 'store/slices/user/user';
import { RuleListRequest } from 'types/rule/rule';
import CloseIcon from '@mui/icons-material/Close';
import { useSphereTitleListQuery } from 'store/slices/cards/sphere/sphere-list';
import { usePersonageTitleListQuery } from 'store/slices/cards/personage/personage-list';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface Props {
    setDataPersonageRequestList?: React.Dispatch<
        React.SetStateAction<CardListRequest | any>
    >;
    cardCategoryList: string[] | undefined;
}
const FilterCard: React.FC<Props> = (props: Props) => {
    const { setDataPersonageRequestList, cardCategoryList } = props;
    const [cardSphereId, setCardSphereId] = useState<string>('');

    const { handleSubmit, control, reset, setValue } = useForm<CardListRequest>(
        {
            defaultValues: {
                sphereId: undefined,
                spherePersonageId: undefined,
                cardTitle: undefined,
                positionInCollection: undefined,
                cardCategory: undefined,
                status: undefined,
            },
        },
    );

    const { data: statusList } = useStatusListQuery();
    const { data: getSphereList } = useSphereTitleListQuery();
    const { data: personageList } = usePersonageTitleListQuery(
        cardSphereId ?? '',
    );

    const location = useLocation();
    const navigate = useNavigate();

    const params = new URLSearchParams(location.search);

    useEffect(() => {
        const obj: Partial<CardListRequest> = {};

        [
            'cardTitle',
            'sphereId',
            'spherePersonageId',
            'positionInCollection',
            'cardCategory',
            'status',
        ].forEach((key) => {
            if (params.get(key)) {
                (obj as any)[key] = params.get(key);
                if (key === 'sphereId') {
                    setCardSphereId(params.get('sphereId') || '');
                }
            }
        });

        reset(obj);
    }, [location.search, reset]);

    const updateParams = (key: string, value?: string | number) => {
        const newParams = new URLSearchParams(window.location.search);

        if (value === undefined || value === '') {
            newParams.delete(key);
        } else {
            newParams.set(key, String(value));
        }

        const newUrl = `${window.location.pathname}${
            newParams.toString() ? `?${newParams.toString()}` : ''
        }`;
        window.history.replaceState({}, '', newUrl);
    };

    const onSubmit = (values: CardListRequest) => {
        if (setDataPersonageRequestList) {
            setDataPersonageRequestList((prevState: RuleListRequest) => ({
                ...prevState,
                cardTitle: values.cardTitle,
                positionInCollection: values.positionInCollection,
                cardCategory:
                    values.cardCategory === ''
                        ? undefined
                        : values.cardCategory,
                status: values.status,
                sphereId: values.sphereId,
                spherePersonageId: values.spherePersonageId,
            }));
        }

        Object.entries(values).forEach(([key, val]) => {
            updateParams(key, val || undefined);
        });
    };

    const resets = () => {
        reset();
        if (setDataPersonageRequestList) {
            setDataPersonageRequestList({ page: 1, pageSize: 10 });
        }
        navigate(location.pathname, { replace: true });
    };

    return (
        <Box
            pt={pxToRem(10)}
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            component="form"
            onSubmit={handleSubmit(onSubmit)}
        >
            <Box display={'flex'} flexBasis={'100%'}>
                <Controller
                    name="cardTitle"
                    control={control}
                    render={({
                        field: { name, value = '', onChange },
                        fieldState: { error },
                    }) => (
                        <TextField
                            label={'Card Title'}
                            name={name}
                            value={value || ''}
                            sx={{ width: '30%' }}
                            onChange={onChange}
                            error={Boolean(error)}
                            helperText={error?.message || ''}
                        />
                    )}
                />

                <FormControl sx={{ ml: pxToRem(10), width: '30%' }} fullWidth>
                    <InputLabel>Sphere</InputLabel>
                    <Controller
                        name="sphereId"
                        control={control}
                        render={({ field: { name, value = '', onChange } }) => (
                            <Box sx={{ position: 'relative' }}>
                                <Select
                                    label="Sphere"
                                    name={name}
                                    value={value}
                                    onChange={(event) => {
                                        const newValue = event.target.value;
                                        onChange(newValue);

                                        setValue(
                                            'spherePersonageId',
                                            undefined,
                                        );

                                        if (setCardSphereId) {
                                            setCardSphereId(newValue);
                                        }
                                    }}
                                    fullWidth
                                    input={
                                        <OutlinedInput
                                            label="Sphere"
                                            endAdornment={
                                                value && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            onChange(undefined);
                                                            setCardSphereId?.(
                                                                '',
                                                            );
                                                            updateParams(
                                                                'sphereId',
                                                            );
                                                        }}
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            top: '50%',
                                                            right: 25,
                                                            transform:
                                                                'translateY(-50%)',
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            }
                                        />
                                    }
                                >
                                    {getSphereList?.map((status) => (
                                        <MenuItem
                                            key={status.id.toString()}
                                            value={status.id.toString()}
                                        >
                                            {status.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        )}
                    />
                </FormControl>

                <FormControl sx={{ ml: pxToRem(10), width: '30%' }} fullWidth>
                    <InputLabel>Personage</InputLabel>
                    <Controller
                        name="spherePersonageId"
                        control={control}
                        render={({ field: { name, value = '', onChange } }) => (
                            <Select
                                label="Personage"
                                name={name}
                                value={value}
                                onChange={onChange}
                                input={
                                    <OutlinedInput
                                        label="Personage"
                                        endAdornment={
                                            value !== '' && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        onChange(undefined);
                                                        updateParams(
                                                            'spherePersonageId',
                                                        );
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        right: 25,
                                                        transform:
                                                            'translateY(-50%)',
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            )
                                        }
                                    />
                                }
                            >
                                {personageList?.map((status) => (
                                    <MenuItem
                                        key={status.id.toString()}
                                        value={status.id.toString()}
                                    >
                                        {status.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                </FormControl>

                <Controller
                    name="positionInCollection"
                    control={control}
                    render={({
                        field: { name, value = '', onChange },
                        fieldState: { error },
                    }) => (
                        <TextField
                            label={'Position Collection'}
                            name={name}
                            value={value || ''}
                            onChange={onChange}
                            error={Boolean(error)}
                            helperText={error?.message || ''}
                            sx={{ ml: pxToRem(10), width: '30%' }}
                        />
                    )}
                />

                <FormControl sx={{ ml: pxToRem(10), width: '30%' }}>
                    <InputLabel>Card Category</InputLabel>
                    <Controller
                        name="cardCategory"
                        control={control}
                        render={({ field: { name, value = '', onChange } }) => (
                            <Select
                                label="Card Category"
                                name={name}
                                value={value}
                                onChange={onChange}
                                input={
                                    <OutlinedInput
                                        label="Card Category"
                                        endAdornment={
                                            value && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        onChange(undefined);
                                                        updateParams(
                                                            'cardCategory',
                                                        );
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        right: 25,
                                                        transform:
                                                            'translateY(-50%)',
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            )
                                        }
                                    />
                                }
                            >
                                {cardCategoryList?.map((status) => (
                                    <MenuItem
                                        key={status.toString()}
                                        value={status.toString()}
                                    >
                                        {status}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                </FormControl>

                <FormControl sx={{ ml: pxToRem(10), width: '30%' }}>
                    <InputLabel>Product Status</InputLabel>
                    <Controller
                        name="status"
                        control={control}
                        render={({ field: { name, value = '', onChange } }) => (
                            <Select
                                label="Product Status"
                                name={name}
                                value={value}
                                onChange={onChange}
                                input={
                                    <OutlinedInput
                                        label="Product Status"
                                        endAdornment={
                                            value && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        onChange(undefined);
                                                        updateParams('status');
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        right: 25,
                                                        transform:
                                                            'translateY(-50%)',
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            )
                                        }
                                    />
                                }
                            >
                                {statusList?.map((status) => (
                                    <MenuItem
                                        key={status.toString()}
                                        value={status.toString()}
                                    >
                                        {status}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                </FormControl>
            </Box>

            <Box display={'flex'} alignItems={'center'} ml={pxToRem(20)}>
                <Button
                    fullWidth
                    sx={{ mr: pxToRem(20), px: pxToRem(20) }}
                    variant={'contained'}
                    type={'submit'}
                >
                    Search
                </Button>
                <Button
                    fullWidth
                    sx={{ px: pxToRem(20) }}
                    variant={'contained'}
                    color={'secondary'}
                    onClick={resets}
                >
                    Clear
                </Button>
            </Box>
        </Box>
    );
};

export default FilterCard;
